import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { ImageRequest, PropertyImage } from "@10x/dubai-rest-api/dist/property_images/entity";
import React, { useEffect, useState } from "react";
import Strings from "@my-dld/assets/strings";
import { exToMessage, getAllImages, getImageUrl, uploadImage } from "@10x/dubai-rest-api/dist/property_images/api";
import { SpinnerLoader } from "../SpinnerLoader";
import { hideLoading, showErrorMessage, showLoading, showMessage } from "@my-dld/utils/UIUtils";
import { RequestHeaders } from "@10x/bcs/dist/utils";
import { getToken } from "@my-dld/utils/User";
import PropertyPhotosModal from "./PropertyPhotosModal";
import Dropzone from "react-dropzone";
import { StringUtil } from "@eres/core";
import { getConsumerId } from "../../configApp";

const MAX_NO_FILES = 10;

interface Props {
	property: AssetCard;
	onViewAll: () => void;
	onUpdateCount: ( count: number ) => void;
}

interface State {
	isLoading: boolean;
	images: PropertyImage[];
	showModal: boolean;
	position: number;
	defPosition: number;
	canAdd: boolean,
}

function PropertyPhotos( props: Props ) {
	const [ state, setState ] = useState<State>( {
		isLoading: true,
		images: [],
		showModal: false,
		position: 0,
		defPosition: 0,
		canAdd: true,
	} );

	const showLoading = ( show: boolean ) => {
		setState( prevState => ( {
			...prevState,
			isLoading: show,
		} ) );
	};

	useEffect( () => {
		const { propertyId, isFreeHold } = props.property;
		getAllImages( "" + propertyId, isFreeHold ).then( r => {
			const nImages = r.data;
			props.onUpdateCount( nImages.length );
			setState( prevState => ( {
				...prevState,
				isLoading: false,
				images: nImages,
				defPosition: getDefaultPosition( nImages ),
				canAdd: canAddImages( nImages ),
			} ) );
		} ).catch( e => {
			showLoading( false );
			if ( e.code !== 404 ) {
				exToMessage( e ).then( message => {
					showMessage( message.error );
				} );
			}
		} );
	}, [ props.property ] );

	const onUploadComplete = ( images: PropertyImage[] ) => {
		props.onUpdateCount( images.length );
		setState( prevState => ( {
			...prevState,
			images: images,
			defPosition: getDefaultPosition( images ),
			canAdd: canAddImages( images ),
		} ) );
	};

	const showModal = ( show: boolean, index: number ) => {
		setState( prevState => ( {
			...prevState,
			showModal: show,
			position: index,
		} ) );
	};

	const onDelete = ( images: PropertyImage[] ) => {
		props.onUpdateCount( images.length );
		setState( prevState => ( {
			...prevState,
			images: images,
			defPosition: getDefaultPosition( images ),
			canAdd: canAddImages( images ),
		} ) );
	};

	const onPrimary = ( images: PropertyImage[] ) => {
		setState( prevState => ( {
			...prevState,
			images: images,
			defPosition: getDefaultPosition( images ),
			showModal: false,
		} ) );
	};

	const size = state.images.length;
	return <>
		<div className="card mb-3 property-images">
			<div className="card-body">
				{ state.isLoading && <div className={ "d-flex " }><SpinnerLoader/></div> }
				{ !state.isLoading && <>
					{ size === 0 && <span>{ Strings.no_property_photos_info }</span> }
					<div className="form-row">
						{ size > 0 && state.images.slice( 0, 5 ).map( ( image: PropertyImage, index: number ) => {
							const isDefault = ( state.defPosition === index );
							const imageUrl = getPropertyImageUrl( image, props.property );
							return <div
								key={ `${ index }-${ image.id }` }
								className={ `col-md-2 col-6 ${ isDefault ? "property-main-img" : "" }` }
								onClick={ () => showModal( true, index ) }>
								<ImageView imageUrl={ imageUrl }/>
								{/*{ isDefault && <span>{ Strings.main }</span> }*/ }
							</div>;
                        })}
                    </div>
                    <div className="card-footer">
                        <RenderUploadImages
                            canAdd={state.canAdd} size={size} property={props.property}
                            onUploadComplete={onUploadComplete}/>
                        {size > 0 && <>
                            <button
                                className="btn btn-link font-size14 p-0" type="button"
                                onClick={props.onViewAll}>{Strings.view_all}</button>
                        </>}
                    </div>
				</> }
			</div>
		</div>
		{ state.showModal && <PropertyPhotosModal
			property={ props.property }
			images={ state.images }
			defPosition={ state.defPosition }
			position={ state.position }
			onDismiss={ () => showModal( false, 0 ) }
			onDelete={ onDelete }
			onPrimary={ onPrimary }/> }
	</>;
}

export function getPropertyImageUrl( pImage: PropertyImage, property?: AssetCard ) {
	if ( property ) {
		const { source } = pImage.metadata;
		const { propertyId, isFreeHold } = property;
		const req: ImageRequest = {
			id: pImage.id,
			source: source,
			propertyId: "" + propertyId,
			isFreeHold: isFreeHold,
		};
		return getImageUrl( req );
	}
	return "";
}

export function getDefaultPosition( images: PropertyImage[] ) {
	const position = images.findIndex( ( img: PropertyImage ) => img.metadata.default );
	return ( position === -1 ) ? 0 : position;
}

export function ImageView( props: { imageUrl: string } ) {
	const [ loading, setLoading ] = useState<boolean>( true );
	const [ url, setUrl ] = useState<string>( "" );

	useEffect( () => {
		fetchImage( props.imageUrl ).then( ( blob: Blob ) => {
			const url = URL.createObjectURL( blob );
			setUrl( url );
			setLoading( false );
		} );
	}, [ props.imageUrl ] );

	if ( loading )
		return <div className="thumbnail-loading"><SpinnerLoader/></div>;
	return <img src={ url } alt="..." className="img-fluid img-thumbnail"/>;
}

export function RenderUploadImages( props: {
	canAdd: boolean, size: number, onUploadComplete: ( images: PropertyImage[] ) => void,
	property?: AssetCard, containerClass?: string;
} ) {
	const { canAdd, size, onUploadComplete, property, containerClass } = props;

	async function uploadImg( req: ImageRequest, file: File ) {
		const data = new FormData();
		data.append( "files", file, file.name );
		const response = await uploadImage( req, data );
		return response.data;
	}

	const onDrop = ( files: File[] ) => {
		showLoading();
		const uploadedDocs: Promise<boolean>[] = [];
		let nImages: PropertyImage[] = [];
		for ( const file of files ) {
			const req: ImageRequest | undefined = getImageRequest( property );
			if ( req ) {
				uploadedDocs.push( uploadImg( req, file ).then( ( images: PropertyImage[] ) => {
					nImages = images;
					return Promise.resolve( true );
				} ).catch( e => {
					return exToMessage( e ).then( message => {
						return Promise.reject( message.error );
					} );
				} ) );
			}
		}
		Promise.allSettled( uploadedDocs ).then( promises => {
			hideLoading();
			if ( nImages.length > 0 )
				onUploadComplete( nImages );
			const error = promises.map( ( promise: PromiseSettledResult<boolean> ) => {
				return promise.status === "rejected" ? promise.reason : "";
			} ).join( "\n" ).trim();
			if ( !StringUtil.isEmptyStringOrNull( error ) ) {
				showMessage( error );
			}
		} );
	};

	const onDropRejected = () => {
		showErrorMessage( Strings.property_photo_upload_validation.replace( "~", "10" ) );
	};

	if ( !canAdd )
		return null;
	return <Dropzone
		multiple={ true }
		maxSize={ 10 * 1024 * 1000 }
		onDropRejected={ onDropRejected }
		onDrop={ onDrop }
		accept={ [ "image/png", "image/jpeg" ] }>
		{ ( { getRootProps, getInputProps } ) => (
			<div className={ containerClass } tabIndex={ 0 } { ...getRootProps() }>
				<input { ...getInputProps() } />
				<button type="button" className="btn btn-grey">
					{ `${ size > 0 ? Strings.add_more : Strings.add_photo }` }
				</button>
			</div>
		) }
	</Dropzone>;
}

export function getImageRequest( property?: AssetCard ): ImageRequest | undefined {
	if ( property ) {
		return {
			source: "wallet",
			propertyId: "" + property.propertyId,
			isFreeHold: property.isFreeHold,
		};
	}
	return undefined;
}

export async function fetchImage( imageUrl: string ): Promise<Blob> {
	const headers: RequestHeaders = { "token": getToken()!, "consumer-id": getConsumerId(imageUrl) ?? ""};
	let response = await fetch( imageUrl, { headers } );
	return await response.blob();
}

export function canAddImages( images: PropertyImage[] ) {
	const walletImages = images.filter( ( img: PropertyImage ) => img.metadata.source === "wallet" );
	// return (MAX_NO_FILES - walletImages.length) > 0;
	return true;
}

export default PropertyPhotos;
